import type { AppEntryReturnDestination } from '@core/entities/AppEntryContext';

const storageKey = 'inav.app-entry.original-origin';
const homeStorageKey = 'inav.app-entry.home-destination';
const allowedDestinationIds: ReadonlySet<string> = new Set(['homepage', 'messages', 'profile']);
const allowedAppPages: ReadonlySet<string> = new Set(['homePage', 'messages', 'account']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isOriginalOrigin(value: unknown): value is AppEntryReturnDestination {
  return isRecord(value)
    && typeof value.id === 'string'
    && allowedDestinationIds.has(value.id)
    && typeof value.label === 'string'
    && typeof value.appPage === 'string'
    && allowedAppPages.has(value.appPage)
    && typeof value.browserUri === 'string';
}

function readDestination(key: string): AppEntryReturnDestination | undefined {
  const storedValue = globalThis.sessionStorage.getItem(key);
  if (!storedValue) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(storedValue) as unknown;
    return isOriginalOrigin(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export class OriginalOriginStore {
  public get(): AppEntryReturnDestination | undefined {
    return readDestination(storageKey);
  }

  public getHome(): AppEntryReturnDestination | undefined {
    return readDestination(homeStorageKey);
  }

  public rememberHome(destinations: readonly AppEntryReturnDestination[]): void {
    const home = destinations.find((destination) => destination.appPage === 'homePage');
    if (!home) {
      globalThis.sessionStorage.removeItem(homeStorageKey);
      return;
    }

    globalThis.sessionStorage.setItem(homeStorageKey, JSON.stringify(home));
  }

  public remember(destinations: readonly AppEntryReturnDestination[]): void {
    const originalOrigin = destinations[0];
    if (!originalOrigin) {
      globalThis.sessionStorage.removeItem(storageKey);
      return;
    }

    globalThis.sessionStorage.setItem(storageKey, JSON.stringify(originalOrigin));
  }
}
